import { differenceInMinutes, format } from 'date-fns';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import { useBuSettings, useMachineInfo } from 'common/pages/fleetV2/providers';
import { getUserTimeZone, isUSTimeZone } from 'common/helpers/v1';
import { MachineStatusProps } from '../types';
import { POLLING_INTERVAL } from '../utils';
import { useGetAccountInfoQuery } from 'api';
import { useDateRange } from 'components';
import { useMachineStateProtein } from 'hooks';
import { useMemo } from 'react';
import { processApiData } from 'common/helpers/processApiData';

export const useMachineStatusProtein = (): MachineStatusProps => {
  const { colors, Icons } = useBuSettings('protein');
  const { machineId, timeZone } = useMachineInfo() as {
    machineId: string;
    timeZone: string;
    businessUnit: string;
  };

  const {
    data: accountData,
    isLoading,
    hasError
  } = useGetAccountInfoQuery({ machineId }, { pollingInterval: POLLING_INTERVAL }) as {
    data: {
      connectionStatus?: {
        watchdog?: string;
        lastKnownConnected?: string;
      };
      currProdState?: string;
    };
    isLoading: boolean;
    hasError?: string;
  };

  const { endTime } = useDateRange().utcTZConvertedISO;

  const localStartDatetimeString = endTime;

  const { states } = useMachineStateProtein(machineId, localStartDatetimeString, 30000);

  const cachedData = useMemo(
    () =>
      processApiData(states as [], {
        startTimeKey: 'timestamp',
        endTimeKey: 'endTimestamp',
        timeZone: timeZone as string
      }),
    [states]
  );

  const MachineCurrentState = cachedData?.[0]?.name?.toString() || 'unknown';

  const state: string = MachineCurrentState ? (MachineCurrentState as string) : 'unknown';

  const isUSA = isUSTimeZone(getUserTimeZone());
  const formattedDate =
    accountData?.connectionStatus?.lastKnownConnected &&
    utcToZonedTime(accountData.connectionStatus.lastKnownConnected, timeZone);

  const time =
    accountData?.connectionStatus?.watchdog === 'good'
      ? undefined
      : formattedDate &&
        (isUSA
          ? format(new Date(formattedDate), 'LL/dd/yy hh:mm:ss a')
          : format(new Date(formattedDate), 'dd/LL/yy HH:mm:ss'));

  const lastConnectedTime = accountData?.connectionStatus?.lastKnownConnected
    ? differenceInMinutes(new Date(), new Date(accountData?.connectionStatus?.lastKnownConnected))
    : null;

  const displayStatus =
    state && lastConnectedTime !== null && lastConnectedTime < 6 ? "Connected" : 'Offline';

  return {
    color:
      accountData?.connectionStatus?.watchdog === 'good' || displayStatus === 'Connected'
        ? colors?.['online']
        : colors?.['disconnected'],
    status: displayStatus,
    Icon:
      accountData?.connectionStatus?.watchdog === 'good' || displayStatus === 'Connected'
        ? Icons?.['connected']
        : Icons?.['disconnected'],
    machineState: accountData?.currProdState,
    timestamp: (accountData?.connectionStatus?.watchdog === 'good' || displayStatus === 'Connected') ? "" :  time,
    isLoading,
    hasError
  };
};
